import React from 'react';
import { format, addDays, isSameDay } from 'date-fns';
import { Video, MessageCircle, Building2, CalendarDays, ClipboardList } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { EmrAppointment } from '@/services/emrService';
import type { CalendarEvent } from '@/services/calendarService';
import type { Task } from '@/services/taskService';
import VisitActionButton from '@/components/visit/VisitActionButton';

interface WeekViewProps {
  weekStart: Date;
  appointments: EmrAppointment[];
  externalEvents?: CalendarEvent[];
  tasks?: Task[];
  onAppointmentClick: (appointment: EmrAppointment) => void;
  onTimeSlotClick: (date: string, time: string) => void;
  onTaskClick?: (task: Task) => void;
}

const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 7 AM to 7 PM
const DAYS = Array.from({ length: 7 }, (_, i) => i); // 0-6 (Sun-Sat)

const statusColors: Record<string, string> = {
  scheduled: '#3b82f6',
  confirmed: '#22c55e',
  checked_in: '#f59e0b',
  in_progress: '#8b5cf6',
  completed: '#6b7280',
  no_show: '#ef4444',
  cancelled: '#9ca3af',
};

const taskPriorityColors: Record<string, string> = {
  high: '#ef4444',
  medium: '#f97316',
  low: '#eab308',
};

const WeekView: React.FC<WeekViewProps> = ({
  weekStart,
  appointments,
  externalEvents = [],
  tasks = [],
  onAppointmentClick,
  onTimeSlotClick,
  onTaskClick,
}) => {
  const weekDays = DAYS.map(i => addDays(weekStart, i));
  const today = new Date();

  // Helper to get appointments for a specific day
  const getAppointmentsForDay = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return appointments.filter(apt => {
      const aptDate = format(new Date(apt.start_time), 'yyyy-MM-dd');
      return aptDate === dateStr;
    });
  };

  // Helper to get tasks for a specific day
  const getTasksForDay = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return tasks.filter(task => task.due_date === dateStr);
  };

  // Helper to get external events for a specific day (exclude 'free')
  const getExternalEventsForDay = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return externalEvents.filter(evt => {
      if (evt.status === 'free') return false;
      const evtDate = format(new Date(evt.start), 'yyyy-MM-dd');
      return evtDate === dateStr;
    });
  };

  // Calculate position/height for an external event on the time grid
  const getExternalEventStyle = (evt: CalendarEvent) => {
    if (evt.isAllDay) {
      return null; // All-day events rendered separately
    }
    const start = new Date(evt.start);
    const end = new Date(evt.end);
    const hour = start.getHours();
    const minute = start.getMinutes();
    const top = ((hour - 7) * 60 + minute) * (180 / 60);
    const durationMinutes = Math.max((end.getTime() - start.getTime()) / 60000, 15);
    const height = (durationMinutes / 60) * 180;
    return {
      top: `${top}px`,
      height: `${Math.max(height, 30)}px`,
    };
  };

  // Detect overlapping appointments and assign columns
  const getOverlapLayout = (apts: EmrAppointment[]) => {
    if (apts.length === 0) return new Map<string, { column: number; totalColumns: number }>();
    
    // Sort by start time, then by duration (longer first)
    const sorted = [...apts].sort((a, b) => {
      const startDiff = new Date(a.start_time).getTime() - new Date(b.start_time).getTime();
      if (startDiff !== 0) return startDiff;
      return (b.duration || 30) - (a.duration || 30);
    });

    const layout = new Map<string, { column: number; totalColumns: number }>();
    const columns: { apt: EmrAppointment; start: number; end: number }[][] = [];
    
    // Assign columns - appointments go to first column where they don't overlap
    for (const apt of sorted) {
      const aptStart = new Date(apt.start_time).getTime();
      const aptEnd = new Date(apt.end_time).getTime();
      
      let assignedColumn = -1;
      for (let c = 0; c < columns.length; c++) {
        const canFit = columns[c].every(slot => aptStart >= slot.end || aptEnd <= slot.start);
        if (canFit) {
          assignedColumn = c;
          columns[c].push({ apt, start: aptStart, end: aptEnd });
          break;
        }
      }
      
      if (assignedColumn === -1) {
        assignedColumn = columns.length;
        columns.push([{ apt, start: aptStart, end: aptEnd }]);
      }
      
      layout.set(apt.id, { column: assignedColumn, totalColumns: 0 });
    }

    // Calculate totalColumns for each appointment based on concurrent overlaps
    for (const apt of sorted) {
      const aptStart = new Date(apt.start_time).getTime();
      const aptEnd = new Date(apt.end_time).getTime();
      
      let maxConcurrent = 0;
      for (const col of columns) {
        const hasOverlap = col.some(slot => aptStart < slot.end && aptEnd > slot.start);
        if (hasOverlap) maxConcurrent++;
      }
      
      const existing = layout.get(apt.id)!;
      layout.set(apt.id, { ...existing, totalColumns: Math.max(maxConcurrent, 1) });
    }
    
    return layout;
  };

  // Helper to calculate position and height
  const getAppointmentStyle = (apt: EmrAppointment, layoutInfo: { column: number; totalColumns: number }) => {
    const start = new Date(apt.start_time);
    const hour = start.getHours();
    const minute = start.getMinutes();

    const top = ((hour - 7) * 60 + minute) * (180 / 60); // 180px per hour
    const height = (apt.duration / 60) * 180;

    const { column, totalColumns } = layoutInfo;
    const columnWidth = (100 - 4) / totalColumns; // 4% total padding
    const left = 2 + column * columnWidth; // 2% left margin
    const width = columnWidth - 2; // 2% gap between columns

    return {
      top: `${top}px`,
      height: `${Math.max(height, 50)}px`, // Min height for clickability
      left: `${left}%`,
      width: `${width}%`,
    };
  };

  return (
    <div className='flex h-full flex-col'>
      {/* Day headers */}
      <div className='flex border-b border-slate-200' style={{ backgroundColor: '#f8f3ed' }}>
        <div className='w-20 flex-shrink-0' />
        {weekDays.map((day, i) => {
          const isToday = isSameDay(day, today);
          return (
            <div key={i} className='flex-1 border-l border-slate-200 px-2 py-2 text-center'>
              <div className={cn('text-[11px] font-semibold uppercase tracking-wider text-slate-500', isToday && 'text-brand-teal')}>
                {format(day, 'EEE')}
              </div>
              <div className={cn(
                'mt-0.5 text-lg font-semibold text-slate-700',
                isToday && 'inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-teal text-white'
              )}>
                {format(day, 'd')}
              </div>
            </div>
          );
        })}
      </div>

      {/* All-day external events row */}
      {weekDays.some(day => getExternalEventsForDay(day).some(e => e.isAllDay)) && (
        <div className='flex border-b border-slate-200'>
          <div className='w-20 flex-shrink-0 flex items-center justify-end pr-3'>
            <span className='text-[10px] font-medium text-slate-400'>All day</span>
          </div>
          {weekDays.map((day, i) => {
            const allDayEvents = getExternalEventsForDay(day).filter(e => e.isAllDay);
            return (
              <div key={i} className='flex-1 border-l border-slate-200 px-1 py-1 space-y-0.5'>
                {allDayEvents.map(evt => {
                  const isTentative = evt.status === 'tentative';
                  return (
                    <div
                      key={`ext-allday-${evt.id}`}
                      className={cn(
                        'flex items-center gap-0.5 rounded px-1 py-0.5 text-[9px]',
                        isTentative ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500',
                      )}
                    >
                      <CalendarDays className={cn('h-2 w-2 flex-shrink-0', isTentative ? 'text-amber-500' : 'text-slate-400')} />
                      <span className='truncate font-medium'>{evt.title || (isTentative ? 'Tentative' : 'Busy')}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Tasks banner row */}
      {weekDays.some(day => getTasksForDay(day).length > 0) && (
        <div className='flex border-b border-orange-100'>
          <div className='w-20 flex-shrink-0 flex items-center justify-end pr-3'>
            <span className='text-[10px] font-medium text-orange-400'>Tasks</span>
          </div>
          {weekDays.map((day, i) => {
            const dayTasks = getTasksForDay(day);
            return (
              <div key={i} className='flex-1 border-l border-slate-200 px-1 py-1 space-y-0.5'>
                {dayTasks.map(task => {
                  const priorityColor = taskPriorityColors[task.priority] || '#f97316';
                  const isCompleted = (task.effective_status || task.status) === 'completed';
                  return (
                    <div
                      key={`task-${task.task_id}`}
                      className={cn(
                        'flex items-center gap-0.5 rounded px-1 py-0.5 text-[9px] cursor-pointer hover:opacity-80 transition-opacity',
                        isCompleted && 'opacity-60',
                      )}
                      style={{
                        backgroundColor: '#f9731612',
                        borderLeft: `2px solid ${priorityColor}`,
                      }}
                      onClick={() => onTaskClick?.(task)}
                    >
                      <ClipboardList className='h-2 w-2 flex-shrink-0 text-orange-500' />
                      <span className={cn('truncate font-medium text-slate-700', isCompleted && 'line-through')}>
                        {task.title}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* Time grid */}
      <div className='flex-1 overflow-y-auto'>
        <div className='flex'>
          {/* Time labels */}
          <div className='w-20 flex-shrink-0'>
            {HOURS.map(hour => (
              <div
                key={hour}
                className='h-[180px] border-b border-slate-100 pr-3 pt-1 text-right text-[11px] font-medium text-slate-400'
              >
                {format(new Date().setHours(hour, 0, 0, 0), 'h a')}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {weekDays.map((day, dayIndex) => {
            const dayAppointments = getAppointmentsForDay(day);
            const dayExternalEvents = getExternalEventsForDay(day);
            const dateStr = format(day, 'yyyy-MM-dd');
            const overlapLayout = getOverlapLayout(dayAppointments);

            return (
              <div key={dayIndex} className='relative flex-1 border-l border-slate-100'>
                {/* Grid lines */}
                {HOURS.map(hour => (
                  <div
                    key={hour}
                    className='h-[180px] cursor-pointer border-b border-slate-100 transition-colors hover:bg-brand-teal/[0.03]'
                    onClick={() =>
                      onTimeSlotClick(dateStr, `${hour.toString().padStart(2, '0')}:00`)
                    }
                  />
                ))}

                {/* External events (behind appointments) */}
                {dayExternalEvents.map(evt => {
                  if (evt.isAllDay) return null;
                  const style = getExternalEventStyle(evt);
                  if (!style) return null;
                  const isTentative = evt.status === 'tentative';
                  return (
                    <div
                      key={`ext-${evt.id}`}
                      className={cn(
                        'absolute left-1 right-1 rounded-md px-1.5 py-1 text-[10px] pointer-events-none',
                        isTentative
                          ? 'bg-amber-50/80 border border-dashed border-amber-300'
                          : 'bg-slate-100/80 border border-dashed border-slate-300',
                      )}
                      style={style}
                    >
                      <div className='flex items-center gap-0.5 truncate'>
                        <CalendarDays className={cn('h-2.5 w-2.5 flex-shrink-0', isTentative ? 'text-amber-500' : 'text-slate-400')} />
                        <span className={cn('truncate font-medium', isTentative ? 'text-amber-600' : 'text-slate-500')}>
                          {evt.title || (isTentative ? 'Tentative' : 'Busy')}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {/* Appointments */}
                {dayAppointments.map(apt => {
                  const layoutInfo = overlapLayout.get(apt.id) || { column: 0, totalColumns: 1 };
                  const style = getAppointmentStyle(apt, layoutInfo);
                  const color =
                    apt.color || apt.serviceType?.color || statusColors[apt.status] || '#4A8A9A';
                  const patientName =
                    apt.patient_name ||
                    (apt.patient
                      ? `${apt.patient.first_name} ${apt.patient.last_name}`
                      : 'Unknown');
                  const totalCols = layoutInfo.totalColumns;
                  const isNarrow = totalCols > 2;
                  const isVeryNarrow = totalCols > 3;

                  return (
                    <div
                      key={apt.id}
                      className={cn(
                        'absolute cursor-pointer overflow-hidden rounded-md text-xs shadow-sm transition-all group',
                        'hover:z-30 hover:shadow-md hover:scale-[1.01]',
                        isVeryNarrow ? 'p-0.5' : 'p-1.5'
                      )}
                      style={{
                        ...style,
                        backgroundColor: color + '18',
                        borderLeft: `3px solid ${color}`,
                      }}
                      onClick={() => onAppointmentClick(apt)}
                      title={`${patientName}\n${format(new Date(apt.start_time), 'h:mm a')} - ${format(new Date(apt.end_time), 'h:mm a')}\n${apt.serviceType?.name || ''}`}
                    >
                      <div className={cn(
                        'flex items-center gap-0.5 truncate font-medium text-slate-700',
                        isVeryNarrow ? 'text-[8px]' : 'text-[10px]'
                      )}>
                        {(apt as { visit_mode?: string }).visit_mode === 'video' && (
                          <Video className={cn('flex-shrink-0 text-blue-500', isVeryNarrow ? 'h-2 w-2' : 'h-2.5 w-2.5')} />
                        )}
                        {(apt as { visit_mode?: string }).visit_mode === 'async_message' && (
                          <MessageCircle className={cn('flex-shrink-0 text-indigo-500', isVeryNarrow ? 'h-2 w-2' : 'h-2.5 w-2.5')} />
                        )}
                        {(apt as { visit_mode?: string }).visit_mode === 'in_person' && (
                          <Building2 className={cn('flex-shrink-0 text-brand-teal', isVeryNarrow ? 'h-2 w-2' : 'h-2.5 w-2.5')} />
                        )}
                        <span className='truncate'>{patientName}</span>
                      </div>
                      <div className={cn(
                        'truncate text-slate-500',
                        isVeryNarrow ? 'text-[7px]' : 'text-[9px]'
                      )}>
                        {format(new Date(apt.start_time), 'h:mm a')}
                      </div>
                      {!isNarrow && apt.serviceType?.name && (
                        <div className='truncate text-[9px] text-slate-400'>
                          {apt.serviceType.name}
                        </div>
                      )}
                      {isNarrow && (
                        <div className='hidden group-hover:block text-[9px] text-slate-400 mt-0.5 bg-inherit'>
                          <div className='truncate'>{apt.serviceType?.name}</div>
                          <div>{format(new Date(apt.end_time), 'h:mm a')}</div>
                        </div>
                      )}
                      {!isVeryNarrow && (
                        <div className='absolute right-0.5 top-0.5 hidden group-hover:block'>
                          <VisitActionButton appointment={apt} variant='icon' stopPropagation />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WeekView;
